import { AxiosInstance, AxiosResponse } from "axios"
import {
  ProjectVersionResponse,
  DatamapCompareResponse,
  DeliverableRecordBody,
  RecordDeliverableResult,
} from "types/projectVersion.types"

/**
 * Fetches the project's version ID: a content hash of {questionnaire + datamap + query
 * definitions + data}, plus a data-excluded comparabilityKey for wave-over-wave comparison.
 * A part that couldn't be read is an error answer, never a stamp with a hole.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - The project
 * @param owner - Owner hint (may be "" — resolved server-side)
 */
export async function getProjectVersion(
  axiosInstance: AxiosInstance,
  projectId: string,
  owner: string
): Promise<ProjectVersionResponse> {
  return axiosInstance.get(
    `/project-version-get?id=${encodeURIComponent(projectId)}&hint_owner=${encodeURIComponent(owner)}`
  )
}

/**
 * Field-level diff of two projects' data datamaps (the binding layer). Direction: base = old,
 * other = new — so changes read as "new in the `other` project".
 */
export async function compareDatamaps(
  axiosInstance: AxiosInstance,
  baseId: string,
  baseOwner: string,
  otherId: string,
  otherOwner: string
): Promise<DatamapCompareResponse> {
  return axiosInstance.get(
    `/datamap-compare?id=${encodeURIComponent(baseId)}&hint_owner=${encodeURIComponent(
      baseOwner
    )}&other=${encodeURIComponent(otherId)}&other_owner=${encodeURIComponent(otherOwner)}`
  )
}

/**
 * Records an exported file in the project history. The server works the version ID out again
 * and refuses the record (409) when it is no longer `body.expectedProjectVersion`, the version
 * read before the export. The answer carries the export record built from the history entry.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - The project
 * @param owner - Owner hint
 * @param filename - The exported file's name, without extension
 * @param format - The export format
 * @param body - The expected version, the applied export map and the file's stamp
 * @param instanceId - The exporting session, so the server can capture its lineage
 */
export async function recordDeliverable(
  axiosInstance: AxiosInstance,
  projectId: string,
  owner: string,
  filename: string,
  format: string,
  body: DeliverableRecordBody,
  instanceId?: string
): Promise<AxiosResponse<RecordDeliverableResult>> {
  const instanceParam = instanceId ? `&instanceId=${encodeURIComponent(instanceId)}` : ""
  return axiosInstance.post(
    `/deliverable-record?id=${encodeURIComponent(projectId)}&hint_owner=${encodeURIComponent(
      owner
    )}&filename=${encodeURIComponent(filename)}&format=${encodeURIComponent(format)}${instanceParam}`,
    body
  )
}
